import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, CircularProgress, TableCell, Typography, styled, useTheme } from "@mui/material";
import MUIDataTable, { MUIDataTableColumnDef } from "mui-datatables";
import { UserBasicInfoModel } from "models/UserBasicInfoModel";
import { createPrefixedPropsFilteredComponent } from "helpers/styledHelpers";

interface UserBasicInfoTableProps {
    users: UserBasicInfoModel[];
    fetchingUsers: boolean;
    title: string;
    setUsersSelected: (usersSelected: number[]) => void;
}

const StyledMUIDataTable = styled(MUIDataTable)`
  & .tss-1cdcmys-MUIDataTable-responsiveBase {
    overflow: visible !important;
  }
  & .MuiTableCell-head span button {
    font-size: 16px !important;
  }
`;

const StyledUserName = createPrefixedPropsFilteredComponent<{ $color: string; $marginLeft: string }>(Typography)(({ $color, $marginLeft }) => ({
  color: $color,
  marginLeft: $marginLeft
}));

export const UserBasicInfoTable = (props: UserBasicInfoTableProps ) => {
    const [users, setUsers] = useState<UserBasicInfoModel[]>([]);
    const { t } = useTranslation();
    const theme = useTheme();
    const { fetchingUsers } = props;

    useEffect(() => {
        setUsers(props.users);
    }, [props.users]);

    const userColumns = [
        { name: "id", label: "Id", options: { filter: false, sort: false, display: 'excluded' } },
        {
            name: "role", 
            label: t('trainingsManagement.roleColumn'), 
            options: { 
                filter: true, 
                sort: true,
                display: 'excluded',
                filterOptions: {
                    names: [t('roles.Admin'), t('roles.Patron'), t('roles.PatronLight'), t('roles.Participant')],
                    renderValue: (role: string) => role,
                    logic(role: string, filters: string[]) {
                        return !filters.includes(role);
                    },
                },
                customBodyRender: (value: any, tableMeta: any, updateValue: any) => {
                    return t(`roles.${value}`);
                }                
            }
        },
        {
            name: "displayName", 
            label: t('trainingsManagement.nameColumn'), 
            options: { 
                filter: true, 
                sort: true, 
                customBodyRender: (value: any, tableMeta: any, updateValue: any) => {
                    const user = users.find(u => u.id === tableMeta.rowData[0]);
                    const role = (user?.role?.toLowerCase() ?? 'participant') as keyof typeof theme.palette.roles;
                    const color = theme.palette.roles[role];
        
                    return (
                        <StyledUserName $color={color} $marginLeft={theme.spacing(1)}>
                            {value}
                        </StyledUserName>
                    );
                }
            }
        },
        {
          name: "tagA", 
          label: t('trainingsManagement.tagAColumn'), 
          options: { 
              filter: true, 
              sort: true
          }
        },
        {
          name: "tagB", 
          label: t('trainingsManagement.tagBColumn'), 
          options: { 
              filter: true, 
              sort: true
          }
        }
    ] as MUIDataTableColumnDef[];

    return <Box>
    {fetchingUsers
    ? <CircularProgress />
    : <StyledMUIDataTable
        title={props.title}
        data={users}
        columns={userColumns}
        options={{  
            responsive: 'standard',     
            textLabels: {
                body: {
                  noMatch: t('dataTable.noMatch'),
                  toolTip: t('dataTable.toolTip'),
                  columnHeaderTooltip: column => t('dataTable.columnHeaderTooltip', { column: column.label })
                },
                pagination: {
                  next: t('dataTable.next'),
                  previous: t('dataTable.previous'),
                  rowsPerPage: t('dataTable.rowsPerPage'),
                  displayRows: t('dataTable.displayRows'),
                },
                toolbar: {
                  search: t('dataTable.search'),
                  downloadCsv: t('dataTable.downloadCsv'),
                  print: t('dataTable.print'),
                  viewColumns: t('dataTable.viewColumns'),
                  filterTable: t('dataTable.filterTable'),
                },
                filter: {
                  all: t('dataTable.all'),
                  title: t('dataTable.title'),
                  reset: t('dataTable.reset'),
                },
                viewColumns: {
                  title: t('dataTable.viewColumnsTitle'),
                  titleAria: t('dataTable.viewColumnsTitleAria'),
                },
                selectedRows: {
                  text: t('dataTable.selectedRowsText'),
                  delete: t('dataTable.delete'),
                  deleteAria: t('dataTable.deleteAria'),
                },
            },
            fixedHeader: false,
            onRowSelectionChange: (curSelectedRows, allRowsSelected, rowsSelected) => {
                if (rowsSelected) {
                    props.setUsersSelected(rowsSelected);
                }
            },
        }}
        />    
    }
    </Box>
}